<script setup>
import { nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

import { DEV_MODE } from '@/config/dev-mode';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';

import {
  CONTENTS as YEARLY_FORTUNE_CONTENTS,
  CONTENTS_PAGE_MAP as YEARLY_FORTUNE_SECTION_PAGE_MAP,
} from './constants';

import Guide01 from './Guide01.vue';
import Guide02 from './Guide02.vue';
import Contents01 from './Contents01.vue';
import Contents02 from './Contents02.vue';
import QuarterEnergy from './QuarterEnergy.vue';
import MonthlyEnergy from './MonthlyEnergy.vue';
import Contents04 from './Contents04.vue';
import Contents05 from './Contents05.vue';
import Contents06 from './Contents06.vue';
import Contents07 from './Contents07.vue';
import Contents08 from './Contents08.vue';
import Ending from './Ending.vue';

import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { isObjectEmpty } from '@/composables/functions';

const props = defineProps({
  updateSelectedDescNav: {
    type: Function,
  },
  hasPermission: {
    type: Boolean,
  },
});
const emit = defineEmits(['goPopAnalysis', 'warning']);

/** 경고 상태 (대운범위초과 등) */
const warningMessage = ref('');

const route = useRoute();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData, yearlyFortuneData } = storeToRefs(dataStore);

// =============== 신년운세 컴포넌트별 데이터 슬라이스 ===============
const contents01Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents03Data = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const monthlyEnergyData = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
}));

const contents04Data = computed(() => ({
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const contents05Data = computed(() => ({
  운세해석: yearlyFortuneData.value?.['3_운세해석'],
}));

const contents06Data = computed(() => ({
  에너지방향: yearlyFortuneData.value?.['4_에너지방향'],
}));

const contents07Data = computed(() => ({
  에너지추천: yearlyFortuneData.value?.['5_에너지추천'],
  안정에너지: yearlyFortuneData.value?.['6_안정에너지'],
}));

const contents08Data = computed(() => ({
  종합전략: yearlyFortuneData.value?.['7_종합전략'],
}));

const guide02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
}));

// =============== 페이지 ===============
/** 페이지 이동 (로그 없이) */
const moveToPage = (id) => {
  // 2페이지부터 데이터가 필요한 구간 → 경고 상태면 진입 차단
  const warning = warningMessage.value || dataStore.yearlyFortuneWarning;
  if (id >= 2 && warning) {
    emit('warning', warning);
    return;
  }
  if (id < 5) {
    // 1~4페이지는 무료
    props.updateSelectedDescNav('');
  } else {
    // 5페이지부터는 결제 필요
    if (!props.hasPermission) {
      emit('goPopAnalysis');
      return;
    }
    // 결제 후에는 오른쪽 패널 표시 (용어해석만 표시)
    props.updateSelectedDescNav('personalityDictionary');
  }
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 또는 페이지네이션) */
const updateIndexNo = (id, tocIdx) => {
  if (tocIdx != null) {
    // 목차 리스트 클릭
    addContentLogAPI({
      contentType: props.hasPermission ? 'ayf_fortune_toc_paid_opt3' : 'ayf_fortune_toc_free_opt3',
      option3: String(tocIdx),
    });
  } else {
    // 이전/다음 화살표 클릭
    addContentLogAPI({
      contentType: 'ayf_fortune_page_opt3',
      option3: String(id),
    });
  }
  moveToPage(id);
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setYearlyFortuneIndex(newIndex);
});
onUnmounted(() => {
  store.setYearlyFortuneIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = YEARLY_FORTUNE_SECTION_PAGE_MAP[newIdx];

    if (target) {
      moveToPage(target);
    } else if (newIdx === null) {
      // selectedContentIndex가 null이면 기본값으로 1페이지 표시
      moveToPage(1);
    }
  },
  { immediate: true },
);

/** 오버뷰로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewYearlyFortune');
};

// =============== 신년운세 데이터 상태 ===============
const guestId = route.params.id;

// =============== 평가하기 데이터 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getYearlyFortuneRate = async () => {
  if (!props.hasPermission) return;
  try {
    const params = {
      serviceName: 'YEARLY_FORTUNE',
      objectKey: `${guestId}`,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postYearlyFortuneRate = async (score) => {
  try {
    const params = {
      score,
      serviceName: 'YEARLY_FORTUNE',
      objectKey: `${guestId}`,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getYearlyFortuneRate();
    }
  } catch (error) {}
};

onMounted(() => {
  getYearlyFortuneRate();
});

const isFetchingYearlyFortune = ref(true);

/** 권한에 따른 suggestionFlag: 권한 없음 = '1', 권한 있음 = '3' */
const suggestionFlag = computed(() => (props.hasPermission ? '3' : '1'));

const yearlyFortuneParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestId,
    lang: 'KOR',
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 9, // 신년운세
    suggestionFlag: suggestionFlag.value,
    year: new Date().getFullYear(),
  };
});

const isReadyToShow = computed(() => {
  // 데이터 로딩이 완료되면 표시 (데이터가 없어도 표시)
  return !isFetchingYearlyFortune.value;
});

/** 신년운세 데이터 초기화 */
const initYearlyFortuneData = async () => {
  await fetchYearlyFortuneData();
};

/** 신년운세 데이터 요청 */
const fetchYearlyFortuneData = async () => {
  isFetchingYearlyFortune.value = true;
  try {
    if (isObjectEmpty(dataStore.yearlyFortuneData)) {
      const result = await fetchBaseServiceAPI(yearlyFortuneParams.value);

      const data = result.data || result.rag_data;
      if (data) {
        // 경고 응답 체크 (대운범위초과 등) - 데이터 저장하지 않고 경고 상태만 저장
        if (data['경고']) {
          warningMessage.value = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
          dataStore.setYearlyFortuneWarning(warningMessage.value);
          return;
        }
        dataStore.setYearlyFortuneData(data);
        dataStore.setYearlyFortuneSubData({});
      }
    }
  } catch (error) {
    DEV_MODE && console.error('/yearlyFortune.vue fetchYearlyFortuneData 실패:', error);
  } finally {
    isFetchingYearlyFortune.value = false;
  }
};

watch(
  sajuData,
  async (newData, oldData) => {
    if (!isObjectEmpty(newData)) {
      await initYearlyFortuneData();
    }
  },
  { immediate: true },
);

watch(
  () => props.hasPermission,
  async (newValue, oldValue) => {
    if (!isObjectEmpty(sajuData.value) && !oldValue && newValue) {
      // 권한 획득 시 기존 데이터 클리어 후 새로 요청 (suggestionFlag 변경 반영)
      dataStore.setYearlyFortuneData({});
      await initYearlyFortuneData();
    }
  },
);
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('yearly-fortune')" class="full-height over-hidden">
    <div v-if="isFetchingYearlyFortune" class="analysis__loading-overlay full-height">
      <div class="loading-olverlay__img"></div>
      신년운세 분석중입니다!
    </div>
    <Guide01 v-if="indexNo === 1" class="full-height" />
    <Guide02 v-if="indexNo === 2" class="full-height" :data="guide02Data" />
    <Contents01
      v-if="indexNo === 3 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents01Data"
    />
    <Contents02
      v-if="indexNo === 4 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents02Data"
    />
    <QuarterEnergy
      v-if="indexNo === 5 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents03Data"
    />
    <MonthlyEnergy
      v-if="indexNo === 6 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="monthlyEnergyData"
    />
    <Contents04
      v-if="indexNo === 7 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents04Data"
    />
    <Contents05
      v-if="indexNo === 8 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents05Data"
    />
    <Contents06
      v-if="indexNo === 9 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents06Data"
    />
    <Contents07
      v-if="indexNo === 10 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents07Data"
    />
    <Contents08
      v-if="indexNo === 11 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :data="contents08Data"
    />
    <Ending
      v-if="indexNo === 12 && isReadyToShow"
      class="full-height"
      :userName="store.userName"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      @postRate="postYearlyFortuneRate"
      @goToOverView="goToOverView"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="YEARLY_FORTUNE_CONTENTS"
      :curPage="indexNo"
      :hasPermission="hasPermission"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/personality.scss';
@import '@/assets/nangman_style/yearly_fortune.scss';
</style>

<style scoped lang="scss">
.full-height {
  height: 100vh;
}
.analysis__loading-overlay {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  font-size: 18px;
  font-weight: 700;
}
.yearly-fortune-content {
  padding: 40px;
}
</style>
